import classNames from 'classnames'
import styles from './DonationFeedItem.module.css'
import useHeightObserver from '../../hooks/useHeightObserver'
import {
  CSSProperties,
  ForwardedRef,
  KeyboardEventHandler,
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Card } from '../Card'
import { DonationFeedItemProps, DonationProps, SkeletonProps } from './types'
import { IconButton } from '../IconButton'
import { Keys } from '../Popup'
import { LayoutStack } from '../layouts'
import { LoadingSkeleton } from '../LoadingSkeleton'
import { TextLink } from '../TextLink'
import { Tooltip } from '../Tooltip'

const COMMENT_PREVIEW_LINES = 1

function SkeletonInner() {
  return (
    <>
      <LoadingSkeleton width="6.25rem" height="4rem" className={styles.skeletonContainer} />
      <LayoutStack className={styles.skeletonContainer} space="200">
        <LoadingSkeleton width="5.7rem" height="1.3rem" />
        <LoadingSkeleton width="5.7rem" height="1.1rem" />
      </LayoutStack>
    </>
  )
}

function SkeletonWithoutHeightChange({ className }: { className?: string }) {
  return (
    <Card className={classNames(styles.card, className)}>
      <SkeletonInner />
    </Card>
  )
}

const SkeletonWithHeightChange = forwardRef(function SkeletonWithHeightObserver(
  { className, style }: SkeletonProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  return (
    <div style={{ ...(style as CSSProperties), overflow: 'visible' }} ref={ref}>
      <Card className={classNames(styles.card, className)}>
        <SkeletonInner />
      </Card>
    </div>
  )
})

const Skeleton = forwardRef(function Skeleton(props: SkeletonProps, ref?: ForwardedRef<HTMLDivElement>) {
  if (props.onChangeSize) return <SkeletonWithHeightChange ref={ref} {...props} />

  return <SkeletonWithoutHeightChange {...props} />
})

function Cta({
  className,
  amount,
  ctaText,
  ctaLink,
}: {
  className?: string
  amount: ReactNode
  ctaText: string
  ctaLink: string
}) {
  return (
    <Card className={classNames(styles.card, className)}>
      <div className={classNames(styles.amountContainer, styles.ctaAmountContainer)}>{amount}</div>
      <div className={styles.ctaContainer}>
        <TextLink href={ctaLink}>{ctaText}</TextLink>
      </div>
    </Card>
  )
}

function CommentInner({
  handleEnter,
  isTooLong,
  isExpanded,
  toggleIsExpanded,
  comment,
}: {
  handleEnter: KeyboardEventHandler<HTMLInputElement>
  isTooLong: boolean
  isExpanded: boolean
  toggleIsExpanded: () => void
  comment: string
}) {
  const iconName = isExpanded ? 'chevronUp' : 'chevronDown'

  if (isExpanded || !isTooLong) {
    return (
      <>
        {comment}
        {isTooLong && (
          <IconButton
            kind="plain"
            size="default"
            iconName={iconName}
            onClick={toggleIsExpanded}
            className={styles.showMoreButton}
          />
        )}
      </>
    )
  }

  return (
    <div className={styles.clickArea} onClick={toggleIsExpanded} onKeyUp={handleEnter} role="button" tabIndex={0}>
      {comment}
      {isTooLong && <IconButton kind="plain" size="default" iconName={iconName} className={styles.showMoreButton} />}
    </div>
  )
}

function CommentWrapper({ comment }: { comment: string | undefined }) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isTooLong, setIsTooLong] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) {
      setIsTooLong(false)
      return
    }

    const lineHeight = parseFloat(getComputedStyle(element).lineHeight)
    const neededLines = Math.ceil(element.scrollHeight / Math.round(lineHeight))

    setIsTooLong(neededLines > COMMENT_PREVIEW_LINES)
  }, [setIsTooLong])

  const toggleIsExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev)
  }, [])

  const handleEnter: KeyboardEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      if (event.key !== Keys.Enter) return
      toggleIsExpanded()
    },
    [toggleIsExpanded]
  )

  if (!comment) return null

  return (
    <div
      ref={ref}
      className={classNames(styles.comment, { [styles.expanded!]: isExpanded })}
      style={{ '--max-preview-lines': COMMENT_PREVIEW_LINES } as CSSProperties}
    >
      <CommentInner
        handleEnter={handleEnter}
        isTooLong={isTooLong}
        isExpanded={isExpanded}
        toggleIsExpanded={toggleIsExpanded}
        comment={comment}
      />
    </div>
  )
}

function Comment({ comment }: { comment: string | undefined }) {
  return (
    <div
      className={classNames(styles.commentContainer, {
        [styles.noComment!]: !comment,
      })}
    >
      <CommentWrapper comment={comment} />
    </div>
  )
}

function Header({ header }: { header: ReactNode }) {
  const [isTruncated, setIsTruncated] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) {
      setIsTruncated(false)
      return
    }
    setIsTruncated(element.scrollWidth > Math.round(element.clientWidth))
  }, [header])

  if (isTruncated) {
    return (
      <Tooltip content={header}>
        <div className={styles.header}>{header}</div>
      </Tooltip>
    )
  }

  return (
    <div ref={ref} className={styles.header}>
      {header}
    </div>
  )
}

function DonationFeedItemInner({ amount, header, timestamp, comment }: DonationProps) {
  return (
    <>
      <div className={styles.amountContainer}>
        <div>{amount}</div>
      </div>
      <LayoutStack space="none" className={styles.secondSlotWrapper}>
        <Header header={header} />
        <div className={styles.timestamp}>{timestamp}</div>
        <Comment comment={comment} />
      </LayoutStack>
    </>
  )
}

function DonationFeedItemInnerWithoutHeightObserver({ className, ...props }: DonationProps) {
  return (
    <Card className={classNames(styles.card, className)}>
      <DonationFeedItemInner {...props} />
    </Card>
  )
}

const DonationFeedItemWithHeightObserver = forwardRef(function DonationFeedItemWithHeightObserver(
  { className, style, onChangeSize, ...props }: DonationProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const height = style?.height ? parseFloat(style?.height) : undefined
  const localRef = useHeightObserver({ externalHeight: height, onChangeSize })

  return (
    <div style={{ ...(style as CSSProperties), overflow: 'visible' }} ref={ref}>
      <Card className={classNames(styles.card, className)} ref={localRef}>
        <DonationFeedItemInner {...props} />
      </Card>
    </div>
  )
})

const DefaultDonationFeedItem = forwardRef(function DefaultDonationFeedItem(
  props: DonationProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  if (props.onChangeSize) return <DonationFeedItemWithHeightObserver ref={ref} {...props} />

  return <DonationFeedItemInnerWithoutHeightObserver {...props} />
})

const DonationFeedItem = forwardRef(function DonationFeedItem(
  { variant, amount, ctaText, ctaLink, className, ...props }: DonationFeedItemProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  if (variant === 'cta') return <Cta amount={amount} ctaText={ctaText} ctaLink={ctaLink} className={className} />
  if (variant === 'skeleton') return <Skeleton ref={ref} className={className} {...(props as SkeletonProps)} />

  const donationProps = { variant, amount, ctaText, ctaLink, className, ...props } as DonationProps

  return <DefaultDonationFeedItem {...donationProps} />
})

export default DonationFeedItem
